/**
 * Player-facing text by string key, the one template filler every client
 * uses (integration plan slice 5): the log lines here (`describe.ts`), the
 * labels, badges and banners of `@ab/ui` (which re-exports these), the
 * terminal client. Code picks a key, never a word: a sentence whose shape
 * differs (a conditional clause, an `x{count}`) has a key per variant, and
 * each locale's text orders the `{placeholders}` as its language needs.
 */
import { type FloorDirection, type Locale, fillPlaceholders, sourceLocale } from '@ab/content-schema';

/** A string table: `<content id>.<field>` and `ui.<component>.<name>` to text. */
export type Strings = Readonly<Record<string, string>>;

/** The display name of a content id (`monster.slime` to `Slime`), or the id itself. */
export const nameOf = (strings: Strings, contentId: string): string => strings[`${contentId}.name`] ?? contentId;

/** Player-facing text by string key (`ui.status.hp`); the key itself when a table lacks it, so a gap shows. */
export const text = (strings: Strings, key: string): string => strings[key] ?? key;

/** Another key's text inside a text, filled with its own values (a floor's name, `ui.floor.down`, inside "{floor} cleared"). */
export type NestedKey = { readonly key: string; readonly values: Readonly<Record<string, number | string>> };

/**
 * Words that fill a text's `{placeholders}`: plain `values` (numbers, and
 * words already in the table's language, such as a unit's name), content ids
 * named through the string table (`names`; a list is joined with
 * `ui.label.separator`), and other UI keys (`keys`: a status or stat name, or
 * a `NestedKey` filled with its own values).
 */
export type TextArgs = {
  readonly values?: Readonly<Record<string, number | string>>;
  readonly names?: Readonly<Record<string, string | readonly string[]>>;
  readonly keys?: Readonly<Record<string, string | NestedKey>>;
};

/**
 * A floor's name as a nested key (ADR 0032): `ui.floor.down` ("B{floor}F", a
 * descending dungeon) or `ui.floor.up` ("{floor}F"), by the dungeon's
 * `direction`; the status line, the floor banners and the floor log lines
 * fill their `{floor}` with it.
 */
export const floorName = (direction: FloorDirection, floor: number): NestedKey => ({ key: `ui.floor.${direction}`, values: { floor } });

/** A floor's name on its own ("B1F"), for a client's own text such as the terminal's title line. */
export const formatFloor = (strings: Strings, direction: FloorDirection, floor: number): string => {
  const name = floorName(direction, floor);
  return formatText(strings, name.key, { values: name.values });
};

/** The text of `key` with its placeholders filled from `args`; a placeholder with no word stays as it is. */
export const formatText = (strings: Strings, key: string, args: TextArgs = {}): string =>
  fillPlaceholders(text(strings, key), (name) => {
    const value = args.values?.[name];
    if (value !== undefined) return String(value);
    const named = args.names?.[name];
    if (typeof named === 'string') return nameOf(strings, named);
    if (named !== undefined) return named.map((id) => nameOf(strings, id)).join(text(strings, 'ui.label.separator'));
    const other = args.keys?.[name];
    if (other === undefined) return undefined;
    return typeof other === 'string' ? text(strings, other) : formatText(strings, other.key, { values: other.values });
  });

/** Words already in the table's language, joined as the table joins a list (`ui.label.separator`). */
export const joinWords = (strings: Strings, words: readonly string[]): string => words.join(text(strings, 'ui.label.separator'));

/**
 * The table a client shows for `locale`: that locale's text over the source
 * language's, so a key not translated yet reads in English rather than as a
 * raw key (content lint and `pnpm strings report` list it as missing).
 */
export const localeStrings = (tables: Readonly<Record<Locale, Strings>>, locale: Locale): Strings =>
  locale === sourceLocale ? tables[sourceLocale] : { ...tables[sourceLocale], ...Object.fromEntries(Object.entries(tables[locale]).filter(([, v]) => v !== '')) };
